import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  LayoutDashboard, Image, CalendarDays, FileText, LogOut, Menu, X, 
  Plus, Trash2, Edit, Upload, ChevronLeft, Save, Eye, Award, Code
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_56a601c8-b253-4088-9fb6-1f7146328396/artifacts/ve1u5sut_logo%20jotuns%20new1.png";

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'gallery', label: 'Galería', icon: Image },
  { id: 'events', label: 'Eventos', icon: CalendarDays },
  { id: 'blog', label: 'Blog', icon: FileText },
  { id: 'sponsors', label: 'Patrocinadores', icon: Award },
  { id: 'embeds', label: 'Embeds', icon: Code },
];

export function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Cargando...</div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-950 border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Jotuns" className="h-8" />
          <span className="font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>ADMIN</span>
        </div>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white"
          data-testid="mobile-sidebar-toggle"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 bottom-0 w-64 bg-zinc-950 border-r border-white/10 z-40
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Jotuns" className="h-12" />
            <div>
              <span className="block font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>JOTUNS</span>
              <span className="text-xs text-orange-500">ADMINISTRACIÓN</span>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeSection === item.id 
                  ? 'bg-orange-500/20 text-orange-500 border-l-4 border-orange-500' 
                  : 'text-zinc-400 hover:bg-white/5 hover:text-white'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="px-4 py-2 mb-4">
            <p className="text-sm text-zinc-400">Sesión iniciada como</p>
            <p className="text-white font-medium truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-red-500/10 hover:text-red-400 transition-colors"
            data-testid="logout-button"
          >
            <LogOut size={20} />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {activeSection === 'dashboard' && <DashboardHome user={user} />}
          {activeSection === 'gallery' && <GalleryManager />}
          {activeSection === 'events' && <EventsManager />}
          {activeSection === 'blog' && <BlogManager />}
          {activeSection === 'sponsors' && <SponsorsManager />}
          {activeSection === 'embeds' && <EmbedsManager />}
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

function DashboardHome({ user }) {
  const [stats, setStats] = useState({ gallery: 0, events: 0, blog: 0 });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [galleryRes, eventsRes, blogRes] = await Promise.all([
        axios.get(`${API}/api/gallery`),
        axios.get(`${API}/api/events`),
        axios.get(`${API}/api/admin/blog`, { withCredentials: true })
      ]);
      setStats({
        gallery: galleryRes.data.length,
        events: eventsRes.data.length,
        blog: blogRes.data.length
      });
    } catch (error) {
      console.log('Error fetching stats');
    }
  };

  return (
    <div data-testid="dashboard-home">
      <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
        BIENVENIDO, {user?.name?.toUpperCase() || 'ADMIN'}
      </h1>
      <p className="text-zinc-400 mb-8">Panel de administración de Jotuns Basketball Club</p>

      <div className="grid sm:grid-cols-3 gap-6 mb-8">
        <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6" data-testid="stat-gallery">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Image className="text-orange-500" size={24} />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{stats.gallery}</p>
              <p className="text-zinc-400 text-sm">Imágenes en galería</p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6" data-testid="stat-events">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <CalendarDays className="text-amber-500" size={24} />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{stats.events}</p>
              <p className="text-zinc-400 text-sm">Eventos programados</p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6" data-testid="stat-blog">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <FileText className="text-green-500" size={24} />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{stats.blog}</p>
              <p className="text-zinc-400 text-sm">Publicaciones de blog</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
          ACCIONES RÁPIDAS
        </h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <button className="p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-white/10 rounded-lg text-left transition-colors">
            <Image className="text-orange-500 mb-2" size={24} />
            <p className="text-white font-medium">Subir imagen</p>
            <p className="text-zinc-500 text-sm">Agregar a galería</p>
          </button>
          <button className="p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-white/10 rounded-lg text-left transition-colors">
            <CalendarDays className="text-amber-500 mb-2" size={24} />
            <p className="text-white font-medium">Crear evento</p>
            <p className="text-zinc-500 text-sm">Agregar al calendario</p>
          </button>
          <button className="p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-white/10 rounded-lg text-left transition-colors">
            <FileText className="text-green-500 mb-2" size={24} />
            <p className="text-white font-medium">Nueva publicación</p>
            <p className="text-zinc-500 text-sm">Crear entrada de blog</p>
          </button>
        </div>
      </div>
    </div>
  );
}

function GalleryManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', image_data: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const { data } = await axios.get(`${API}/api/gallery`);
      setImages(data);
    } catch (error) {
      console.error('Error fetching gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      if (editingImage) {
        await axios.put(`${API}/api/gallery/${editingImage.id}`, {
          title: formData.title,
          description: formData.description
        }, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/gallery`, formData, { withCredentials: true });
      }
      fetchImages();
      resetForm();
    } catch (error) {
      console.error('Error saving image');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta imagen?')) {
      try {
        await axios.delete(`${API}/api/gallery/${id}`, { withCredentials: true });
        fetchImages();
      } catch (error) {
        console.error('Error deleting image');
      }
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', image_data: '' });
    setEditingImage(null);
    setShowForm(false);
  };

  const startEdit = (image) => {
    setEditingImage(image);
    setFormData({ title: image.title, description: image.description || '', image_data: '' });
    setShowForm(true);
  };

  return (
    <div data-testid="gallery-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            GALERÍA
          </h1>
          <p className="text-zinc-400">Gestiona las imágenes de la galería</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="add-image-btn"
        >
          <Plus size={20} className="mr-2" />
          Nueva Imagen
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                {editingImage ? 'EDITAR IMAGEN' : 'NUEVA IMAGEN'}
              </h2>
              <button onClick={resetForm} className="text-zinc-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <Label className="text-zinc-300">Título</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 text-white"
                  required
                  data-testid="image-title-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Descripción (opcional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 text-white"
                  rows={3}
                  data-testid="image-description-input"
                />
              </div>
              {!editingImage && (
                <div>
                  <Label className="text-zinc-300">Imagen</Label>
                  <div className="mt-2 border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-orange-500/50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="image-upload"
                      required
                      data-testid="image-file-input"
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      {formData.image_data ? (
                        <img src={formData.image_data} alt="Preview" className="max-h-48 mx-auto rounded" />
                      ) : (
                        <>
                          <Upload className="mx-auto text-zinc-500 mb-2" size={32} />
                          <p className="text-zinc-400">Haz clic para seleccionar una imagen</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              )}
              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="flex-1 border-white/20 text-zinc-300 hover:bg-white/5"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="save-image-btn"
                >
                  {uploading ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Images Grid */}
      {loading ? (
        <div className="text-zinc-400 text-center py-12">Cargando...</div>
      ) : images.length === 0 ? (
        <div className="text-zinc-400 text-center py-12 bg-zinc-900/50 border border-white/10 rounded-xl">
          <Image size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay imágenes en la galería</p>
          <p className="text-sm mt-2">Haz clic en "Nueva Imagen" para agregar</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div 
              key={image.id} 
              className="group relative bg-zinc-900/50 border border-white/10 rounded-lg overflow-hidden"
              data-testid={`gallery-item-${image.id}`}
            >
              <img
                src={image.image_url}
                alt={image.title}
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  onClick={() => startEdit(image)}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
                  data-testid={`edit-image-${image.id}`}
                >
                  <Edit size={20} />
                </button>
                <button
                  onClick={() => handleDelete(image.id)}
                  className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-red-400"
                  data-testid={`delete-image-${image.id}`}
                >
                  <Trash2 size={20} />
                </button>
              </div>
              <div className="p-3">
                <p className="text-white font-medium truncate">{image.title}</p>
                {image.description && (
                  <p className="text-zinc-400 text-sm truncate">{image.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventsManager() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', date: '', time: '', location: '', event_type: 'entrenamiento'
  });
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API}/api/events`);
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData };
      if (editingEvent) {
        await axios.put(`${API}/api/events/${editingEvent.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/events`, payload, { withCredentials: true });
      }
      fetchEvents();
      resetForm();
    } catch (error) {
      console.error('Error saving event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este evento?')) {
      try {
        await axios.delete(`${API}/api/events/${id}`, { withCredentials: true });
        fetchEvents();
      } catch (error) {
        console.error('Error deleting event');
      }
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', date: '', time: '', location: '', event_type: 'entrenamiento' });
    setEditingEvent(null);
    setShowForm(false);
    setSelectedDate(null);
  };

  const startEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time,
      location: event.location,
      event_type: event.event_type
    });
    try {
      setSelectedDate(parseISO(event.date));
    } catch {}
    setShowForm(true);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (date) {
      setFormData({ ...formData, date: format(date, 'yyyy-MM-dd') });
    }
  };

  const eventTypes = [
    { value: 'entrenamiento', label: 'Entrenamiento' },
    { value: 'partido', label: 'Partido' },
    { value: 'torneo', label: 'Torneo' },
    { value: 'reunion', label: 'Reunión' },
    { value: 'otro', label: 'Otro' }
  ];

  return (
    <div data-testid="events-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            EVENTOS
          </h1>
          <p className="text-zinc-400">Gestiona el calendario de eventos</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="add-event-btn"
        >
          <Plus size={20} className="mr-2" />
          Nuevo Evento
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                {editingEvent ? 'EDITAR EVENTO' : 'NUEVO EVENTO'}
              </h2>
              <button onClick={resetForm} className="text-zinc-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label className="text-zinc-300">Título</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 text-white"
                  required
                  data-testid="event-title-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 text-white"
                  rows={3}
                  required
                  data-testid="event-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Fecha</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-2 justify-start text-left font-normal border-white/20 bg-transparent text-white hover:bg-white/5"
                        data-testid="event-date-trigger"
                      >
                        <CalendarDays className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, 'PPP', { locale: es }) : 'Seleccionar fecha'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-zinc-900 border-white/10">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        locale={es}
                        data-testid="event-date-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-zinc-300">Hora</Label>
                  <Input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="mt-2 bg-transparent border-white/20 text-white"
                    required
                    data-testid="event-time-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Ubicación</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 text-white"
                  required
                  data-testid="event-location-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Tipo de evento</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger className="mt-2 bg-transparent border-white/20 text-white" data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {eventTypes.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="flex-1 border-white/20 text-zinc-300 hover:bg-white/5"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="save-event-btn"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Events List */}
      {loading ? (
        <div className="text-zinc-400 text-center py-12">Cargando...</div>
      ) : events.length === 0 ? (
        <div className="text-zinc-400 text-center py-12 bg-zinc-900/50 border border-white/10 rounded-xl">
          <CalendarDays size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay eventos programados</p>
          <p className="text-sm mt-2">Haz clic en "Nuevo Evento" para crear uno</p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div 
              key={event.id}
              className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 flex items-start gap-6"
              data-testid={`event-item-${event.id}`}
            >
              <div className="flex-shrink-0 w-16 text-center bg-zinc-800 rounded-lg py-3">
                <div className="text-2xl font-bold text-orange-500">
                  {format(parseISO(event.date), 'd', { locale: es })}
                </div>
                <div className="text-xs text-zinc-500 uppercase">
                  {format(parseISO(event.date), 'MMM', { locale: es })}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-white">{event.title}</h3>
                <p className="text-zinc-400 mt-1">{event.description}</p>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-zinc-500">
                  <span>{event.time}</span>
                  <span>{event.location}</span>
                  <span className="capitalize px-2 py-0.5 bg-zinc-800 rounded">{event.event_type}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => startEdit(event)}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  data-testid={`edit-event-${event.id}`}
                >
                  <Edit size={20} />
                </button>
                <button
                  onClick={() => handleDelete(event.id)}
                  className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  data-testid={`delete-event-${event.id}`}
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '', content: '', excerpt: '', image_data: '', published: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/blog`, { withCredentials: true });
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingPost) {
        await axios.put(`${API}/api/blog/${editingPost.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/blog`, formData, { withCredentials: true });
      }
      fetchPosts();
      resetForm();
    } catch (error) {
      console.error('Error saving post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta publicación?')) {
      try {
        await axios.delete(`${API}/api/blog/${id}`, { withCredentials: true });
        fetchPosts();
      } catch (error) {
        console.error('Error deleting post');
      }
    }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', excerpt: '', image_data: '', published: false });
    setEditingPost(null);
    setShowForm(false);
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      image_data: '',
      published: post.published
    });
    setShowForm(true);
  };

  return (
    <div data-testid="blog-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            BLOG
          </h1>
          <p className="text-zinc-400">Gestiona las publicaciones del blog</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="add-post-btn"
        >
          <Plus size={20} className="mr-2" />
          Nueva Publicación
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                {editingPost ? 'EDITAR PUBLICACIÓN' : 'NUEVA PUBLICACIÓN'}
              </h2>
              <button onClick={resetForm} className="text-zinc-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label className="text-zinc-300">Título</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 text-white"
                  required
                  data-testid="post-title-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Extracto (resumen corto)</Label>
                <Textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 text-white"
                  rows={2}
                  required
                  data-testid="post-excerpt-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Contenido</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 text-white"
                  rows={8}
                  required
                  data-testid="post-content-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Imagen destacada (opcional)</Label>
                <div className="mt-2 border-2 border-dashed border-white/20 rounded-lg p-6 text-center hover:border-orange-500/50 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="post-image-upload"
                    data-testid="post-image-input"
                  />
                  <label htmlFor="post-image-upload" className="cursor-pointer">
                    {formData.image_data ? (
                      <img src={formData.image_data} alt="Preview" className="max-h-32 mx-auto rounded" />
                    ) : editingPost?.image_url ? (
                      <div>
                        <img src={editingPost.image_url} alt="Current" className="max-h-32 mx-auto rounded mb-2" />
                        <p className="text-zinc-500 text-sm">Haz clic para cambiar la imagen</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="mx-auto text-zinc-500 mb-2" size={24} />
                        <p className="text-zinc-400 text-sm">Haz clic para seleccionar una imagen</p>
                      </>
                    )}
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.published}
                  onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
                  data-testid="post-published-switch"
                />
                <Label className="text-zinc-300">Publicar inmediatamente</Label>
              </div>
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="flex-1 border-white/20 text-zinc-300 hover:bg-white/5"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="save-post-btn"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Posts List */}
      {loading ? (
        <div className="text-zinc-400 text-center py-12">Cargando...</div>
      ) : posts.length === 0 ? (
        <div className="text-zinc-400 text-center py-12 bg-zinc-900/50 border border-white/10 rounded-xl">
          <FileText size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay publicaciones</p>
          <p className="text-sm mt-2">Haz clic en "Nueva Publicación" para crear una</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div 
              key={post.id}
              className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 flex items-start gap-6"
              data-testid={`post-item-${post.id}`}
            >
              {post.image_url && (
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-bold text-white">{post.title}</h3>
                  {post.published ? (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Publicado</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-zinc-700 text-zinc-400 text-xs rounded">Borrador</span>
                  )}
                </div>
                <p className="text-zinc-400">{post.excerpt}</p>
                <p className="text-zinc-500 text-sm mt-2">
                  {format(parseISO(post.created_at), "d 'de' MMMM, yyyy", { locale: es })}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => startEdit(post)}
                  className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  data-testid={`edit-post-${post.id}`}
                >
                  <Edit size={20} />
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  data-testid={`delete-post-${post.id}`}
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SponsorsManager() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [formData, setFormData] = useState({ name: '', website: '', logo_data: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const { data } = await axios.get(`${API}/api/sponsors?active_only=false`, { withCredentials: true });
      setSponsors(data);
    } catch (error) {
      console.error('Error fetching sponsors');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, logo_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingSponsor) {
        await axios.put(`${API}/api/sponsors/${editingSponsor.id}`, {
          name: formData.name,
          website: formData.website
        }, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/sponsors`, formData, { withCredentials: true });
      }
      fetchSponsors();
      resetForm();
    } catch (error) {
      console.error('Error saving sponsor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este patrocinador?')) {
      try {
        await axios.delete(`${API}/api/sponsors/${id}`, { withCredentials: true });
        fetchSponsors();
      } catch (error) {
        console.error('Error deleting sponsor');
      }
    }
  };

  const toggleActive = async (sponsor) => {
    try {
      await axios.put(`${API}/api/sponsors/${sponsor.id}`, {
        active: !sponsor.active
      }, { withCredentials: true });
      fetchSponsors();
    } catch (error) {
      console.error('Error updating sponsor');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', website: '', logo_data: '' });
    setEditingSponsor(null);
    setShowForm(false);
  };

  const startEdit = (sponsor) => {
    setEditingSponsor(sponsor);
    setFormData({ name: sponsor.name, website: sponsor.website || '', logo_data: '' });
    setShowForm(true);
  };

  return (
    <div data-testid="sponsors-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            PATROCINADORES
          </h1>
          <p className="text-zinc-400">Gestiona los logos de patrocinadores</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="add-sponsor-btn"
        >
          <Plus size={20} className="mr-2" />
          Nuevo Patrocinador
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                {editingSponsor ? 'EDITAR PATROCINADOR' : 'NUEVO PATROCINADOR'}
              </h2>
              <button onClick={resetForm} className="text-zinc-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <Label className="text-zinc-300">Nombre del patrocinador</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 text-white"
                  required
                  data-testid="sponsor-name-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Sitio web (opcional)</Label>
                <Input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://ejemplo.com"
                  className="mt-2 bg-transparent border-white/20 text-white"
                  data-testid="sponsor-website-input"
                />
              </div>
              {!editingSponsor && (
                <div>
                  <Label className="text-zinc-300">Logo del patrocinador</Label>
                  <div className="mt-2 border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-orange-500/50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="sponsor-logo-upload"
                      required
                      data-testid="sponsor-logo-input"
                    />
                    <label htmlFor="sponsor-logo-upload" className="cursor-pointer">
                      {formData.logo_data ? (
                        <img src={formData.logo_data} alt="Preview" className="max-h-32 mx-auto rounded" />
                      ) : (
                        <>
                          <Upload className="mx-auto text-zinc-500 mb-2" size={32} />
                          <p className="text-zinc-400">Haz clic para seleccionar el logo</p>
                          <p className="text-zinc-500 text-sm mt-1">Recomendado: formato PNG con fondo transparente</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              )}
              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="flex-1 border-white/20 text-zinc-300 hover:bg-white/5"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="save-sponsor-btn"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sponsors List */}
      {loading ? (
        <div className="text-zinc-400 text-center py-12">Cargando...</div>
      ) : sponsors.length === 0 ? (
        <div className="text-zinc-400 text-center py-12 bg-zinc-900/50 border border-white/10 rounded-xl">
          <Award size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay patrocinadores</p>
          <p className="text-sm mt-2">Haz clic en "Nuevo Patrocinador" para agregar uno</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {sponsors.map((sponsor) => (
            <div 
              key={sponsor.id} 
              className={`group relative bg-zinc-900/50 border rounded-lg overflow-hidden ${
                sponsor.active ? 'border-white/10' : 'border-red-500/30 opacity-60'
              }`}
              data-testid={`sponsor-item-${sponsor.id}`}
            >
              <div className="p-4 flex items-center justify-center h-32 bg-white/5">
                <img
                  src={sponsor.logo_url}
                  alt={sponsor.name}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="p-3 border-t border-white/10">
                <p className="text-white font-medium truncate">{sponsor.name}</p>
                {sponsor.website && (
                  <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="text-zinc-500 text-sm truncate block hover:text-orange-500">
                    {sponsor.website}
                  </a>
                )}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={sponsor.active}
                      onCheckedChange={() => toggleActive(sponsor)}
                      data-testid={`toggle-sponsor-${sponsor.id}`}
                    />
                    <span className="text-xs text-zinc-500">{sponsor.active ? 'Activo' : 'Inactivo'}</span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => startEdit(sponsor)}
                      className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                      data-testid={`edit-sponsor-${sponsor.id}`}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(sponsor.id)}
                      className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      data-testid={`delete-sponsor-${sponsor.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmbedsManager() {
  const [embeds, setEmbeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEmbed, setEditingEmbed] = useState(null);
  const [formData, setFormData] = useState({ title: '', embed_code: '', section: 'custom' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEmbeds();
  }, []);

  const fetchEmbeds = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/embeds`, { withCredentials: true });
      setEmbeds(data);
    } catch (error) {
      console.error('Error fetching embeds');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingEmbed) {
        await axios.put(`${API}/api/embeds/${editingEmbed.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/embeds`, formData, { withCredentials: true });
      }
      fetchEmbeds();
      resetForm();
    } catch (error) {
      console.error('Error saving embed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este embed?')) {
      try {
        await axios.delete(`${API}/api/embeds/${id}`, { withCredentials: true });
        fetchEmbeds();
      } catch (error) {
        console.error('Error deleting embed');
      }
    }
  };

  const toggleActive = async (embed) => {
    try {
      await axios.put(`${API}/api/embeds/${embed.id}`, {
        active: !embed.active
      }, { withCredentials: true });
      fetchEmbeds();
    } catch (error) {
      console.error('Error updating embed');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', embed_code: '', section: 'custom' });
    setEditingEmbed(null);
    setShowForm(false);
  };

  const startEdit = (embed) => {
    setEditingEmbed(embed);
    setFormData({ title: embed.title, embed_code: embed.embed_code, section: embed.section });
    setShowForm(true);
  };

  return (
    <div data-testid="embeds-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            EMBEDS
          </h1>
          <p className="text-zinc-400">Gestiona códigos embed externos (FIBA, formularios, etc.)</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="add-embed-btn"
        >
          <Plus size={20} className="mr-2" />
          Nuevo Embed
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
        <h3 className="text-blue-400 font-semibold mb-2">¿Cómo usar los embeds?</h3>
        <p className="text-zinc-400 text-sm">
          Puedes pegar código embed de FIBA 3x3, formularios de Google, videos de YouTube, etc.
          El código se mostrará en la sección "Inscripciones y Más" de la página principal.
        </p>
        <div className="mt-3 p-3 bg-black/30 rounded font-mono text-xs text-zinc-400 overflow-x-auto">
          {`<script data-fiba-embedtype="registration" data-fiba-eventid="..." src="https://play.fiba3x3.com/embed.js" async></script>`}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                {editingEmbed ? 'EDITAR EMBED' : 'NUEVO EMBED'}
              </h2>
              <button onClick={resetForm} className="text-zinc-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <Label className="text-zinc-300">Título descriptivo</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ej: Inscripción Torneo FIBA 3x3"
                  className="mt-2 bg-transparent border-white/20 text-white"
                  required
                  data-testid="embed-title-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Código Embed</Label>
                <Textarea
                  value={formData.embed_code}
                  onChange={(e) => setFormData({ ...formData, embed_code: e.target.value })}
                  placeholder='<script data-fiba-embedtype="registration" ...'
                  className="mt-2 bg-transparent border-white/20 text-white font-mono text-sm"
                  rows={6}
                  required
                  data-testid="embed-code-input"
                />
                <p className="text-zinc-500 text-xs mt-2">
                  Pega aquí el código HTML/Script que deseas incrustar
                </p>
              </div>
              <div>
                <Label className="text-zinc-300">Sección</Label>
                <Select
                  value={formData.section}
                  onValueChange={(value) => setFormData({ ...formData, section: value })}
                >
                  <SelectTrigger className="mt-2 bg-transparent border-white/20 text-white" data-testid="embed-section-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="custom" className="text-white hover:bg-white/10">Sección personalizada</SelectItem>
                    <SelectItem value="hero" className="text-white hover:bg-white/10">Hero</SelectItem>
                    <SelectItem value="about" className="text-white hover:bg-white/10">Sobre Nosotros</SelectItem>
                    <SelectItem value="calendar" className="text-white hover:bg-white/10">Calendario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="flex-1 border-white/20 text-zinc-300 hover:bg-white/5"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="save-embed-btn"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Embeds List */}
      {loading ? (
        <div className="text-zinc-400 text-center py-12">Cargando...</div>
      ) : embeds.length === 0 ? (
        <div className="text-zinc-400 text-center py-12 bg-zinc-900/50 border border-white/10 rounded-xl">
          <Code size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay embeds configurados</p>
          <p className="text-sm mt-2">Haz clic en "Nuevo Embed" para agregar código externo</p>
        </div>
      ) : (
        <div className="space-y-4">
          {embeds.map((embed) => (
            <div 
              key={embed.id}
              className={`bg-zinc-900/50 border rounded-xl p-6 ${
                embed.active ? 'border-white/10' : 'border-red-500/30 opacity-60'
              }`}
              data-testid={`embed-item-${embed.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-white">{embed.title}</h3>
                    <span className="px-2 py-0.5 bg-zinc-700 text-zinc-400 text-xs rounded capitalize">
                      {embed.section}
                    </span>
                    {!embed.active && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                        Inactivo
                      </span>
                    )}
                  </div>
                  <div className="bg-black/30 rounded p-3 overflow-x-auto">
                    <code className="text-xs text-zinc-400 whitespace-pre-wrap break-all">
                      {embed.embed_code.length > 200 
                        ? embed.embed_code.substring(0, 200) + '...' 
                        : embed.embed_code}
                    </code>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Switch
                    checked={embed.active}
                    onCheckedChange={() => toggleActive(embed)}
                    data-testid={`toggle-embed-${embed.id}`}
                  />
                  <button
                    onClick={() => startEdit(embed)}
                    className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    data-testid={`edit-embed-${embed.id}`}
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(embed.id)}
                    className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    data-testid={`delete-embed-${embed.id}`}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
